import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import UpgradePromptModal from '../../components/UpgradePromptModal/UpgradePromptModal';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config/api';
import './BuyerDirectoryPage.css';

const BuyerDirectoryPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { hasPermission, planDisplayName, getFeature, canViewContactsForRegion, loading: subscriptionLoading } = useSubscription();

  // View state: 'hero' = landing page, 'results' = search results
  const [view, setView] = useState('hero');
  const [keyword, setKeyword] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Results-page filter state (inline search bar)
  const [resultsKeyword, setResultsKeyword] = useState('');
  const [resultsCountry, setResultsCountry] = useState('');
  const [resultsCategory, setResultsCategory] = useState('');
  const [showResultsCountryDD, setShowResultsCountryDD] = useState(false);
  const [showResultsCategoryDD, setShowResultsCategoryDD] = useState(false);

  // API data
  const [sponsoredBuyers, setSponsoredBuyers] = useState([]);
  const [allBuyers, setAllBuyers] = useState([]);
  const [totalBuyers, setTotalBuyers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);

  // Subscription helpers - admins bypass all restrictions immediately
  const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
  const hasBuyerAccess = isAdminUser || (!subscriptionLoading && hasPermission('buyerDirectoryAccess'));
  const canViewContacts = isAdminUser || (!subscriptionLoading && hasPermission('canViewBuyerContacts'));
  const contactRegion = getFeature('buyerContactRegion') || null;

  // DEBUG: Log admin detection for troubleshooting
  console.log('[BuyerDirectory] user.adminRole:', user?.adminRole, '| isAdminUser:', isAdminUser, '| hasBuyerAccess:', hasBuyerAccess, '| subscriptionLoading:', subscriptionLoading);

  // Safety: If admin user, ALWAYS force modal closed
  useEffect(() => {
    if (isAdminUser && showUpgradeModal) {
      console.log('[BuyerDirectory] Admin detected - force closing upgrade modal');
      setShowUpgradeModal(false);
    }
  }, [isAdminUser, showUpgradeModal]);

  const canViewBuyerContact = (buyerCountry) => {
    if (!canViewContacts) return false;
    return canViewContactsForRegion(buyerCountry);
  };

  const ITEMS_PER_PAGE = 10;

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [countriesRes, categoriesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/buyers/filters/countries`),
          fetch(`${API_BASE_URL}/buyers/filters/categories`)
        ]);
        if (countriesRes.ok) {
          const data = await countriesRes.json();
          setCountries(data.length > 0 ? data : ['Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Bahrain', 'Oman']);
        }
        if (categoriesRes.ok) {
          const data = await categoriesRes.json();
          setCategories(data.length > 0 ? data : ['Building Materials', 'Construction Equipment', 'Steel & Metals', 'Electrical', 'Plumbing', 'HVAC']);
        }
      } catch (error) {
        console.error('Error fetching filters:', error);
        setCountries(['Saudi Arabia', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Bahrain', 'Oman']);
        setCategories(['Building Materials', 'Construction Equipment', 'Steel & Metals', 'Electrical', 'Plumbing', 'HVAC']);
      }
    };
    fetchFilters();
  }, []);

  // Fetch buyers from API (requires auth token)
  const fetchBuyers = useCallback(async (searchKeyword, searchCountry, searchCategory, page) => {
    // Use token from context, fallback to localStorage for admin users
    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      // Not authenticated - skip fetch
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchKeyword) params.append('keyword', searchKeyword);
      if (searchCountry) params.append('country', searchCountry);
      if (searchCategory) params.append('category', searchCategory);
      params.append('limit', ITEMS_PER_PAGE);
      params.append('offset', (page - 1) * ITEMS_PER_PAGE);

      const headers = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      const res = await fetch(`${API_BASE_URL}/buyers?${params.toString()}`, { headers });
      if (res.status === 403 && !isAdminUser) {
        // No buyer directory access - show upgrade modal (never for admins)
        setShowUpgradeModal(true);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setSponsoredBuyers(data.sponsoredBuyers || []);
        setAllBuyers(data.regularBuyers || []);
        setTotalBuyers(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching buyers:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Contact masking is now done server-side. These helpers use the server's _contactsMasked flag.
  const getMaskedName = (name) => hasBuyerAccess ? name : 'xxxxxxxxxxxxxxxx';
  const getMaskedEmail = (buyer) => buyer._contactsMasked ? '****@****.com' : (buyer.email || 'N/A');
  const getMaskedPhone = (buyer) => buyer._contactsMasked ? '***-***-****' : (buyer.phoneNumber || buyer.phone || 'N/A');
  const shouldMaskContact = (buyer) => buyer._contactsMasked;
  const getBuyerKeywords = (buyer) => [buyer.category, buyer.subCategory].filter(Boolean);
  const getBuyerYears = (buyer) => buyer.memberYears ? `${String(buyer.memberYears).padStart(2, '0')} Year${buyer.memberYears > 1 ? 's' : ''}` : '01 Year';

  const handleBuyerClick = (buyer) => {
    if (!hasBuyerAccess) { setShowUpgradeModal(true); return; }
    navigate(`/seller/${buyer.id}`);
  };

  // Hero search → switch to results view
  const handleSearch = () => {
    if (!token && !isAdminUser) {
      setShowUpgradeModal(true);
      return;
    }
    if (!hasBuyerAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setResultsKeyword(keyword);
    setResultsCountry(selectedCountry);
    setResultsCategory(selectedCategory);
    setCurrentPage(1);
    setView('results');
    fetchBuyers(keyword, selectedCountry, selectedCategory, 1);
  };

  // Results page inline search
  const handleResultsSearch = () => {
    if (!hasBuyerAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setCurrentPage(1);
    fetchBuyers(resultsKeyword, resultsCountry, resultsCategory, 1);
  };

  // Go back to hero
  const handleBackToHero = () => {
    setView('hero');
  };

  const totalPages = Math.max(1, Math.ceil(totalBuyers / ITEMS_PER_PAGE));

  // Re-fetch when page changes
  useEffect(() => {
    if (view === 'results') {
      fetchBuyers(resultsKeyword, resultsCountry, resultsCategory, currentPage);
    }
  }, [currentPage, view, fetchBuyers, resultsKeyword, resultsCountry, resultsCategory]);

  // Chevron SVG for breadcrumb
  const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Dropdown chevron
  const ChevronDown = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 9L12 15L18 9" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  /* ============================
     HERO VIEW (Image 1)
     ============================ */
  if (view === 'hero') {
    return (
      <div className="buyer-directory-page">
        <Header />
        <div className="bd-breadcrumb-bar">
          <nav className="bd-breadcrumb">
            <Link to="/" className="bd-breadcrumb-item">Home</Link>
            <ChevronRight />
            <span className="bd-breadcrumb-item bd-breadcrumb-active">Buyer Directory</span>
          </nav>
        </div>
        <section className="bd-hero-section">
          <img src="/uploads/builder-images/3d2755e0de8b7f53e498fe9851bfb567c4f8c124_w3852.png" alt="" className="bd-hero-bg" />
          <div className="bd-hero-overlay"></div>
          <div className="bd-hero-content">
            <div className="bd-hero-inner">
              <div className="bd-hero-text">
                <h1 className="bd-hero-title">Buyer Directory</h1>
                <p className="bd-hero-subtitle">Find the right buyers for your products and services locally, regionally and globally.</p>
              </div>
              <div className="bd-search-card">
                <div className="bd-search-fields">
                  <div className="bd-search-row">
                    <div className="bd-select-wrapper" onClick={() => { setShowCountryDropdown(!showCountryDropdown); setShowCategoryDropdown(false); }}>
                      <span className={`bd-select-text ${selectedCountry ? '' : 'bd-placeholder'}`}>{selectedCountry || 'Select Country'}</span>
                      <ChevronDown />
                      {showCountryDropdown && (
                        <div className="bd-dropdown">
                          {countries.map((c) => (
                            <div key={c} className="bd-dropdown-item" onClick={(e) => { e.stopPropagation(); setSelectedCountry(c); setShowCountryDropdown(false); }}>{c}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="bd-select-wrapper" onClick={() => { setShowCategoryDropdown(!showCategoryDropdown); setShowCountryDropdown(false); }}>
                    <span className={`bd-select-text ${selectedCategory ? '' : 'bd-placeholder'}`}>{selectedCategory || 'Select category'}</span>
                    <ChevronDown />
                    {showCategoryDropdown && (
                      <div className="bd-dropdown">
                        {categories.map((c) => (
                          <div key={c} className="bd-dropdown-item" onClick={(e) => { e.stopPropagation(); setSelectedCategory(c); setShowCategoryDropdown(false); }}>{c}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="bd-input-wrapper">
                    <input type="text" placeholder="Enter your products keyword" value={keyword} onChange={(e) => setKeyword(e.target.value)} className="bd-keyword-input" onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
                  </div>
                </div>
                <button className="bd-search-btn" onClick={handleSearch}>Search By Keyword</button>
              </div>
            </div>
          </div>
        </section>
        <Footer />
        <UpgradePromptModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} feature="Buyer Directory Access" currentPlan={planDisplayName || 'Basic'} requiredPlan="GCC Business" />
      </div>
    );
  }

  /* ============================
     RESULTS VIEW (Image 2)
     ============================ */
  return (
    <div className="buyer-directory-page">
      <Header />
      <main className="bdr-main">
        {/* Breadcrumb */}
        <div className="bdr-breadcrumb-container">
          <nav className="bdr-breadcrumb">
            <Link to="/" className="bdr-breadcrumb-link">Home</Link>
            <ChevronRight />
            <span className="bdr-breadcrumb-link bdr-breadcrumb-clickable" onClick={handleBackToHero}>Buyer Directory</span>
            <ChevronRight />
            <span className="bdr-breadcrumb-link">{resultsCategory || 'Building Materials'}</span>
            <ChevronRight />
            <span className="bdr-breadcrumb-current">{resultsKeyword || 'Cement'}</span>
          </nav>
        </div>

        {/* Content */}
        <div className="bdr-content">
          {/* Left Column */}
          <div className="bdr-left">
            {/* Title */}
            <div className="bdr-title-section">
              <h1 className="bdr-page-title">Buyer Directory</h1>
              <p className="bdr-page-desc">Find your desired category and explore products and services.</p>
            </div>

            {/* Search Filter Bar */}
            <div className="bdr-search-bar">
              <div className="bdr-filter-inputs">
                <div className="bdr-filter-input-wrap">
                  <input type="text" placeholder="Cement" value={resultsKeyword} onChange={(e) => setResultsKeyword(e.target.value)} className="bdr-filter-input" />
                </div>
                <div className="bdr-filter-select" onClick={() => { setShowResultsCountryDD(!showResultsCountryDD); setShowResultsCategoryDD(false); }}>
                  <span className="bdr-filter-select-text">{resultsCountry || 'Select Country'}</span>
                  <ChevronDown />
                  {showResultsCountryDD && (
                    <div className="bdr-filter-dropdown">
                      {countries.map((c) => (
                        <div key={c} className="bdr-filter-dd-item" onClick={(e) => { e.stopPropagation(); setResultsCountry(c); setShowResultsCountryDD(false); }}>{c}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bdr-filter-select" onClick={() => { setShowResultsCategoryDD(!showResultsCategoryDD); setShowResultsCountryDD(false); }}>
                  <span className="bdr-filter-select-text">{resultsCategory || 'Select Category'}</span>
                  <ChevronDown />
                  {showResultsCategoryDD && (
                    <div className="bdr-filter-dropdown">
                      {categories.map((c) => (
                        <div key={c} className="bdr-filter-dd-item" onClick={(e) => { e.stopPropagation(); setResultsCategory(c); setShowResultsCategoryDD(false); }}>{c}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button className="bdr-search-btn" onClick={handleResultsSearch}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 21L15 15M3 10C3 10.9193 3.18106 11.8295 3.53284 12.6788C3.88463 13.5281 4.40024 14.2997 5.05025 14.9497C5.70026 15.5998 6.47194 16.1154 7.32122 16.4672C8.1705 16.8189 9.08075 17 10 17C10.9193 17 11.8295 16.8189 12.6788 16.4672C13.5281 16.1154 14.2997 15.5998 14.9497 14.9497C15.5998 14.2997 16.1154 13.5281 16.4672 12.6788C16.8189 11.8295 17 10.9193 17 10C17 9.08075 16.8189 8.1705 16.4672 7.32122C16.1154 6.47194 15.5998 5.70026 14.9497 5.05025C14.2997 4.40024 13.5281 3.88463 12.6788 3.53284C11.8295 3.18106 10.9193 3 10 3C9.08075 3 8.1705 3.18106 7.32122 3.53284C6.47194 3.88463 5.70026 4.40024 5.05025 5.05025C4.40024 5.70026 3.88463 6.47194 3.53284 7.32122C3.18106 8.1705 3 9.08075 3 10Z" stroke="white" strokeOpacity="0.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Search</span>
              </button>
            </div>

            {/* Sponsored Buyers */}
            <div className="bdr-buyers-list">
              {loading && <p style={{textAlign:'center',padding:'20px',color:'#666'}}>Loading buyers...</p>}
              {!loading && sponsoredBuyers.length === 0 && allBuyers.length === 0 && (
                <p style={{textAlign:'center',padding:'20px',color:'#666'}}>No buyers found. Try different search criteria.</p>
              )}
              {sponsoredBuyers.map((buyer) => (
                <div key={buyer.id} className={`bdr-card bdr-card-sponsored ${!hasBuyerAccess ? 'bdr-locked' : ''}`} onClick={() => handleBuyerClick(buyer)}>
                  <div className="bdr-card-header">
                    <div className="bdr-card-info">
                      <img src={buyer.logo || '/uploads/builder-images/435ad5be8193012a0ce26a0fa29d39ebbf91e166_w112.png'} alt={getMaskedName(buyer.name)} className="bdr-card-logo" />
                      <div className="bdr-card-details">
                        <h3 className="bdr-card-name">{getMaskedName(buyer.name)}</h3>
                        <p className="bdr-card-location">{buyer.country} / {getBuyerYears(buyer)}</p>
                      </div>
                    </div>
                    <span className="bdr-sponsored-badge">Sponsored</span>
                  </div>
                  <div className="bdr-card-body">
                    <p className="bdr-card-meta"><span className="bdr-label">Keyword: </span>{getBuyerKeywords(buyer).join(', ') || 'N/A'}</p>
                    <p className="bdr-card-meta"><span className="bdr-label">Category: </span>{buyer.category || 'N/A'}</p>
                    <div className="bdr-card-contact">
                      <p className="bdr-card-meta"><span className="bdr-label">Email: </span><span className={shouldMaskContact(buyer) ? 'bdr-masked' : ''}>{getMaskedEmail(buyer)}</span></p>
                      <p className="bdr-card-meta"><span className="bdr-label">Phone: </span><span className={shouldMaskContact(buyer) ? 'bdr-masked' : ''}>{getMaskedPhone(buyer)}</span></p>
                    </div>
                    {buyer.isVerified && <span className="bdr-verified-badge">Verified</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* All Buyers */}
            <div className="bdr-all-section">
              <h2 className="bdr-all-title">
                {hasBuyerAccess
                  ? (contactRegion === 'gcc' ? 'All buyers — Contact details visible for GCC countries only' : 'All buyers in your accessible region')
                  : 'Upgrade to GCC Business or Global Enterprise to see full buyer names and contact details.'}
              </h2>
              <div className="bdr-buyers-list">
                {allBuyers.map((buyer) => (
                  <div key={buyer.id} className="bdr-card bdr-card-all" onClick={() => handleBuyerClick(buyer)}>
                    <div className="bdr-card-all-content">
                      <div className="bdr-card-all-left">
                        <div className="bdr-checkbox">
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="1" y="1" width="16" height="16" rx="2" stroke="#6D6D6D" strokeWidth="1.5"/>
                          </svg>
                        </div>
                        <div className="bdr-card-details">
                          <h3 className="bdr-card-name">{buyer.name}</h3>
                          <p className="bdr-card-location">{buyer.country} / {getBuyerYears(buyer)}</p>
                        </div>
                      </div>
                      <div className="bdr-card-all-meta">
                        <p className="bdr-card-meta"><span className="bdr-label">Keyword: </span>{getBuyerKeywords(buyer).join(', ') || 'N/A'}</p>
                        <p className="bdr-card-meta"><span className="bdr-label">Category: </span>{buyer.category || 'N/A'}</p>
                        <div className="bdr-card-contact">
                          <p className="bdr-card-meta"><span className="bdr-label">Email: </span><span className={shouldMaskContact(buyer) ? 'bdr-masked' : ''}>{getMaskedEmail(buyer)}</span></p>
                          <p className="bdr-card-meta"><span className="bdr-label">Phone: </span><span className={shouldMaskContact(buyer) ? 'bdr-masked' : ''}>{getMaskedPhone(buyer)}</span></p>
                        </div>
                        {buyer.isVerified && <span className="bdr-verified-badge">Verified</span>}
                      </div>
                    </div>
                    <button className="bdr-view-btn" onClick={(e) => { e.stopPropagation(); handleBuyerClick(buyer); }}>View Details</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="bdr-sidebar">
            {/* Welcome Card */}
            <div className="bdr-welcome-card">
              <div className="bdr-welcome-info">
                <h3 className="bdr-welcome-title">{user ? `Welcome, ${user.companyName || user.email?.split('@')[0]}` : 'Welcome, Guest'}</h3>
                <p className="bdr-welcome-sub">{user ? `Plan: ${planDisplayName}` : "You're not signed in"}</p>
              </div>
              <div className="bdr-welcome-msg">
                {user ? (
                  canViewContacts ? (
                    contactRegion === 'gcc'
                      ? <p>✓ You can view contact details for <strong>GCC buyers only</strong> with your {planDisplayName} plan.</p>
                      : <p>✓ You can view contact details for <strong>all buyers worldwide</strong> with your {planDisplayName} plan.</p>
                  ) : <p>Upgrade to GCC Business or Global Enterprise to see buyer contact details.</p>
                ) : <p>You can see full company names if you login as a premium member.</p>}
              </div>
              {(!user || !canViewContacts) && (
                <button className="bdr-upgrade-btn" onClick={() => navigate('/membership')}>{user ? 'Upgrade your Plan' : 'Login / Sign Up'}</button>
              )}
            </div>

            {/* Ad Banners */}
            <div className="bdr-ads">
              <img src="/uploads/builder-images/6a794d1fa5fc44a0b0ee4b374752f66c6cc8a75e_w570.png" alt="Ad" className="bdr-ad" />
              <img src="/uploads/builder-images/85c0050b3eb2c65c275006565ddf4e75a4a838ad_w570.png" alt="Ad" className="bdr-ad bdr-ad-tall" />
            </div>

            {/* Recommended */}
            <div className="bdr-recommended">
              <h3 className="bdr-recommended-title">Recommended content</h3>
              <div className="bdr-recommended-items">
                <img src="/uploads/builder-images/3758b271dc0adc00a0f825f9ffa9bd656c9b1b68_w506.png" alt="Recommended" className="bdr-recommended-item" />
                <img src="/uploads/builder-images/3758b271dc0adc00a0f825f9ffa9bd656c9b1b68_w506.png" alt="Recommended" className="bdr-recommended-item" />
                <img src="/uploads/builder-images/3758b271dc0adc00a0f825f9ffa9bd656c9b1b68_w506.png" alt="Recommended" className="bdr-recommended-item" />
              </div>
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div className="bdr-pagination">
          <button className="bdr-page-arrow bdr-page-prev" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="#676767" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
            <button key={p} className={`bdr-page-num ${currentPage === p ? 'bdr-page-active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
          ))}
          <button className="bdr-page-arrow bdr-page-next" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 12L10 8L6 4" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      </main>
      <Footer />
      <UpgradePromptModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} feature="Buyer Directory Access" currentPlan={planDisplayName || 'Basic'} requiredPlan="GCC Business" />
    </div>
  );
};

export default BuyerDirectoryPage;
